/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Chip, Input, Pagination, Popover, PopoverContent, PopoverTrigger, Spinner, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow, Tooltip } from '@nextui-org/react';
import { IconSearch, IconDownload } from '@tabler/icons-react';
import { IconTeacher, IconSchool } from '../../../../assets/icons';
import toast from 'react-hot-toast';
import moment from 'moment';
import { Constants, classNames, formatAmount, getCountryFlagUrl, getCurrencySymbol } from '../../../../utils';
import { AdminReportsService } from '../../../../services';
import { generateSchoolPaymentsPDF, generateTeacherPaymentsPDF } from './pdf-reports';
import PopPaymentStatus from './popover-payment-status';
import CountryFlag from '../../../../components/country-flag';
import Render from '../../../../components/render';

const AdminPaymentsReports = () => {
  const { canResetFilter, data, filterBy, goToPage, isLoading, pagination, reload, filters, payments } = useFetchTable();


  const generatePDF = () => {
    if (filters.level_id === Constants.USER.LEVELS.PROFESSOR) {
      generateTeacherPaymentsPDF(data);
      return;
    }

    if (filters.level_id === Constants.USER.LEVELS.SCHOOL) {
      generateSchoolPaymentsPDF(data);
      return;
    }
  }

  return (
    <div>
      {isLoading && (
        <div className="w-screen h-screen fixed inset-0 z-[70] flex justify-center items-center bg-white/30">
          <Spinner />
        </div>
      )}

      <Filters
        canResetFilter={canResetFilter}
        filterBy={filterBy}
        resetFilter={() => reload()}
        currentLevel={filters.level_id}
      />

      {
        filters.level_id === Constants.USER.LEVELS.SCHOOL &&
        <SchoolTable
          data={data}
          payments={payments}
          generatePDF={generatePDF}
          reload={reload}
        />
      }

      {
        filters.level_id === Constants.USER.LEVELS.PROFESSOR &&
        <TeacherTable
          data={data}
          payments={payments}
          generatePDF={generatePDF}
          reload={reload}
        />
      }


      <div className="flex w-full justify-center mt-4">
        <Pagination
          showControls
          variant="bordered"
          page={pagination.page}
          total={pagination.pages}
          onChange={goToPage}
        />
      </div>
    </div>
  );
}


const SchoolTable = ({ data, generatePDF, payments, reload }) => {

  return (
    <div>
      <div className='flex items-center w-full gap-10 mb-4 justify-center'>
        <div className='flex flex-col items-center'>
          <p className='text-xl text-secondary font-bold'>Pagos recibidos</p>
          <p className='text-3xl font-bold'>${formatAmount(payments.totalPayments)}</p>
        </div>
        {/* <div className='flex flex-col items-center'>
          <p className='text-xl text-secondary font-bold'>Deudas por cobrar</p>
          <p className='text-3xl font-bold'>${formatAmount(payments.totalPaymentsDue)}</p>
        </div> */}
      </div>
      <Table
        aria-label="Años Escolares"
        color="primary"
        topContentPlacement="inside"
        topContent={
          <div className="flex flex-row justify-between items-center gap-4">
            <h3 className="text-xl font-medium">Pagos registrados</h3>
            <Button
              className="pr-2 bg-green font-medium text-white"
              endContent={<IconDownload color="white" />}
              onPress={generatePDF}
            >
              Descargar en PDF
            </Button>
          </div>
        }
      >
        <TableHeader>
          <TableColumn>Fecha de pago</TableColumn>
          <TableColumn>Nombre del CE</TableColumn>
          <TableColumn>ID del CE</TableColumn>
          <TableColumn>Forma de pago</TableColumn>
          <TableColumn>Cuenta receptora</TableColumn>
          <TableColumn>Referencia</TableColumn>
          <TableColumn>Monto $</TableColumn>
          <TableColumn>Concepto</TableColumn>
          <TableColumn>Tipo de Plan</TableColumn>
          <TableColumn>Fecha de Vencimiento</TableColumn>
          <TableColumn>Estatus</TableColumn>
        </TableHeader>
        <TableBody items={data} emptyContent="No hay colegios registrados">
          {(school) => {
            const isPending = school?.status === Constants.PAYMENT_STATUS.PENDING;
            const isLocal = Object.values(Constants.LOCAL_PAYMENT_METHODS).includes(school?.payment_method_id);
            const popStatId = `pop-stat-${school.id}`;

            return (
              <TableRow key={school.id}>
                <TableCell>{moment(school?.created_at).format('DD-MM-YYYY')}</TableCell>
                <TableCell>{school?.user?.fullname}</TableCell>
                <TableCell>{school?.user?.dea_code}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span>{school?.payment_method.name}</span>
                    <Render condition={isLocal}>
                      <CountryFlag src={getCountryFlagUrl(school?.payment_method?.country_id)} />
                    </Render>
                  </div>
                </TableCell>
                <TableCell><ReceptorAccount receptor={school?.receptor} /></TableCell>
                <TableCell>{ school?.reference }</TableCell>
                <TableCell>
                  <span>{formatAmount(school?.amount)} </span>
                  <Render condition={isLocal}>
                    <small>({ formatAmount(school?.amount_local, getCurrencySymbol(school?.payment_method?.country_id)) })</small>
                  </Render>
                </TableCell>
                <TableCell>{ school?.concept }</TableCell>
                <TableCell>{ school?.plan_users?.length ? school?.plan_users[0]?.plan?.name : '--' }</TableCell>
                <TableCell>{ school?.plan_users?.length ? moment(school?.plan_users[0]?.next_payment).format('DD-MM-YYYY') : '--' }</TableCell>
                <TableCell>
                  {!isPending && school?.status_text}
                  {isPending && (
                    <Popover showArrow backdrop="opaque">
                      <PopoverTrigger>
                        <Chip id={popStatId} color="danger" variant="flat" className="cursor-pointer">
                          { school?.status_text }
                        </Chip>
                      </PopoverTrigger>
                      <PopoverContent>
                        <PopPaymentStatus
                          payment={school}
                          onSuccess={() => {
                            reload(true);
                            document.getElementById(popStatId)?.click();
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  )}
                </TableCell>
              </TableRow>
            )
          }}
        </TableBody>
      </Table>
    </div>
  )
}

const TeacherTable = ({ data, generatePDF, payments, reload }) => {

  return (
    <div>
      <div className='flex items-center w-full gap-10 mb-4 justify-center'>
        <div className='flex flex-col items-center'>
          <p className='text-xl text-secondary font-bold'>Pagos recibidos</p>
          <p className='text-3xl font-bold'>${formatAmount(payments.totalPayments)}</p>
        </div>
        {/* <div className='flex flex-col items-center'>
          <p className='text-xl text-secondary font-bold'>Deudas por cobrar</p>
          <p className='text-3xl font-bold'>${formatAmount(payments.totalPaymentsDue)}</p>
        </div> */}
      </div>
      <Table
        aria-label="Años Escolares"
        color="primary"
        topContentPlacement="inside"
        topContent={
          <div className="flex flex-row justify-between items-center gap-4">
            <h3 className="text-xl font-medium">Pagos registrados</h3>
            <Button
              className="pr-2 bg-green font-medium text-white"
              endContent={<IconDownload color="white" />}
              onPress={generatePDF}
            >
              Descargar en PDF
            </Button>
          </div>
        }
      >
        <TableHeader>
          <TableColumn>Fecha de pago</TableColumn>
          <TableColumn>Nombre del profesor</TableColumn>
          <TableColumn>Documento</TableColumn>
          <TableColumn>Forma de pago</TableColumn>
          <TableColumn>Cuenta receptora</TableColumn>
          <TableColumn>Referencia</TableColumn>
          <TableColumn>Monto $</TableColumn>
          <TableColumn>Concepto</TableColumn>
          <TableColumn>Tipo de Plan</TableColumn>
          <TableColumn>Fecha de Vencimiento</TableColumn>
          <TableColumn>Estatus</TableColumn>
        </TableHeader>
        <TableBody items={data} emptyContent="No hay profesores registrados">
          {(user) => {
            const isPending = user?.status === Constants.PAYMENT_STATUS.PENDING;
            const popStatId = `pop-stat-${user.id}`;

            return (
              <TableRow key={user.id}>
                <TableCell>{ moment(user?.created_at).format('DD-MM-YYYY') }</TableCell>
                <TableCell>{ user?.user?.fullname }</TableCell>
                <TableCell>{ user?.user?.document_type?.name}{user?.user?.document }</TableCell>
                <TableCell>{ user?.payment_method.name }</TableCell>
                <TableCell><ReceptorAccount receptor={user?.receptor} /></TableCell>
                <TableCell>{ user?.reference }</TableCell>
                <TableCell>{ formatAmount(user?.amount) }</TableCell>
                <TableCell>{ user?.concept }</TableCell>
                <TableCell>{ user?.plan_users?.length ? user?.plan_users[0]?.plan?.name : '--' }</TableCell>
                <TableCell>{ user?.plan_users?.length ? moment(user?.plan_users[0]?.next_payment).format('DD-MM-YYYY') : '--' }</TableCell>
                <TableCell>
                  {!isPending && user?.status_text}
                  {isPending && (
                    <Popover placement="bottom" showArrow backdrop="opaque">
                      <PopoverTrigger>
                        <Chip id={popStatId} color="danger" variant="flat" className="cursor-pointer">
                          { user?.status_text }
                        </Chip>
                      </PopoverTrigger>
                      <PopoverContent>
                        <PopPaymentStatus
                          payment={user}
                          onSuccess={() => {
                            reload(true);
                            document.getElementById(popStatId)?.click();
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  )}
                </TableCell>
              </TableRow>
            )
          }}
        </TableBody>
      </Table>
    </div>
  )
}

const ReceptorAccount = ({ receptor }) => (
  <Tooltip
    content={(
      <div className="px-1 py-2">
        <div className="text-sm">
          <span className="font-semibold">Beneficiario: </span>
          { receptor?.name ?? '--' }
        </div>
        <div className="text-sm">
          <span className="font-semibold">Entidad bancaria: </span>
          { receptor?.bank?.name ?? '--' }
        </div>
        <div className="text-sm">
          <span className="font-semibold">Número de cuenta: </span>
          { receptor?.account_number ?? '--' }
        </div>
        <div className="text-sm">
          <span className="font-semibold">DNI/CI: </span>
          { receptor?.dni ?? '--' }
        </div>
        <div className="text-sm">
          <span className="font-semibold">Teléfono: </span>
          { receptor?.phone ?? '--' }
        </div>
        <div className="text-sm">
          <span className="font-semibold">Correo: </span>
          { receptor?.email ?? '--' }
        </div>
      </div>
    )}
  >
    <p className="text-blue cursor-pointer">{ receptor?.name }</p>
  </Tooltip>
)

const Filters = ({ canResetFilter, filterBy, resetFilter, currentLevel }) => {
  const initialFilter = {
    search: '',
    dea_code: '',
    since: '',
    until: '',
    level_id: Constants.USER.LEVELS.SCHOOL
  };
  const [form, setForm] = useState(initialFilter);

  const onChange = (value, target) => {
    setForm(s => ({ ...s, [target]: value }));
    filterBy(value, target);
  }

  const labelText = useMemo(() => {
    switch (currentLevel) {
      case Constants.USER.LEVELS.SCHOOL:
        return 'Nombre del centro educativo';
      case Constants.USER.LEVELS.PROFESSOR:
        return 'Nombre o documento de usuario';
      default:
        return 'Buscar';
    }
  }, [currentLevel])

  return (
    <section className="mb-4">
      <div className='grid sm:flex grid-cols-1 lg:flex-row items-end gap-4'>
        <Input
          classNames={{
            base: 'w-full sm:max-w-[15rem]',
            inputWrapper: 'border-1 h-10',
          }}
          label={labelText}
          labelPlacement="outside"
          placeholder="Buscar"
          startContent={<IconSearch />}
          variant="bordered"
          radius="sm"
          value={form.search}
          onValueChange={v => onChange(v, 'search')}
        />

        {currentLevel === Constants.USER.LEVELS.SCHOOL && (
          <Input
            classNames={{
              base: 'w-full sm:max-w-[15rem]',
              inputWrapper: 'border-1 h-10',
            }}
            label="ID del CE"
            labelPlacement="outside"
            placeholder="ID del CE"
            variant="bordered"
            radius="sm"
            value={form.dea_code}
            onValueChange={v => onChange(v, 'dea_code')}
          />
        )}

        <Input
          type="date"
          classNames={{
            base: 'w-full sm:max-w-[10rem]',
            inputWrapper: 'border-1 h-10',
            input: `pr-0 text-${!!form.since ? '[]' : 'foreground-400'}`,
          }}
          label="Desde"
          labelPlacement="outside"
          placeholder=" "
          variant="bordered"
          radius="sm"
          value={form.since}
          onValueChange={v => onChange(v, 'since')}
        />
        <Input
          type="date"
          classNames={{
            base: 'w-full md:max-w-[10rem]',
            inputWrapper: 'border-1 h-10',
            input: `pr-0 text-${!!form.until ? '[]' : 'foreground-400'}`,
          }}
          label="Hasta"
          labelPlacement="outside"
          placeholder=" "
          variant="bordered"
          radius="sm"
          value={form.until}
          onValueChange={v => onChange(v, 'until')}
        />

        {canResetFilter && (
          <Button
            variant="light"
            className="text-primaryDark"
            onClick={() => {
              setForm(initialFilter);
              resetFilter();
            }}
          >
            Limpiar filtros
          </Button>
        )}
      </div>
      <div className='grid sm:flex grid-cols-2 lg:flex-row items-end gap-4 mt-4'>
        <UserLevelButton
          level={Constants.USER.LEVELS.SCHOOL}
          form={form}
          onChange={onChange}
          icon={IconSchool}
          label={
            <div className="flex flex-col items-start leading-4">
              <span>Centros</span>
              <span>Educativos</span>
            </div>
          }
        />
        <UserLevelButton
          level={Constants.USER.LEVELS.PROFESSOR}
          form={form}
          onChange={onChange}
          icon={IconTeacher}
          label="Profesores"
        />
      </div>
    </section>
  )
}

const UserLevelButton = ({ level, form, onChange, icon, label }) => {
  return (
    <Button
      className={classNames(
        "w-full md:max-w-[10rem]",
        form.level_id === level ? "bg-green border-green text-white" : "bg-white"
      )}
      variant="bordered"
      radius="sm"
      onPress={() => onChange(level, "level_id")}
      startContent={
        <img
          src={icon}
          className={form.level_id === level ? "invert brightness-0" : ""}
          alt=""
        />
      }
    >
      {label}
    </Button>
  );
};

const useFetchTable = () => {
  const initialFilters = {
    page: 1,
    perPage: Constants.PER_PAGE,
    search: '',
    dea_code: '',
    since: '',
    until: '',
    level_id: Constants.USER.LEVELS.SCHOOL
  };

  const initialPagination = {
    page: 1,
    pages: 1,
    total: 0,
    perPage: Constants.PER_PAGE,
  };

  const [data, setData] = useState([]);
  const [payments, setPayments] = useState({
    totalPayments: 0,
    totalPaymentsDue: 0
  })
  const [canFetch, setCanFetch] = useState(true);
  const [filters, setFilters] = useState(initialFilters);
  const [pagination, setPagination] = useState(initialPagination);

  const debounceTime = 500;
  const debounce = useRef();

  const fetchData = async () => {
    if (!canFetch) return;
    setCanFetch(false);


    try {
      const response = await AdminReportsService.adminReports.getPayments(filters)
      const { data, totalPayments, totalPaymentsDue, ...rest } = response;

      setPayments({ totalPayments, totalPaymentsDue })
      setData(data);
      setPagination(rest);
      setCanFetch(true);

    } catch (error) {
      setData([]);
      onError(String(error));
    }
  }


  const getCurrentPagination = () => {
    // Truco para obtener el estado actualizado (pagination es mantenida con el estado actual por el componente Pagination)
    let pag;
    setPagination(s => {
      pag = s;
      return s;
    });
    return pag;
  }

  const canResetFilter = () => {
    const { page, perPage, ...initial } = initialFilters;
    const { page: _, perPage: __, ...current } = filters;
    delete initial.level_id;
    delete current.level_id;
    const initFilter = JSON.stringify(initial);
    const currFilter = JSON.stringify(current);
    return initFilter !== currFilter;
  }

  const onError = (msg) => toast.error(msg);

  const reload = (inSamePage = false) => {
    setCanFetch(true);
    if (!inSamePage) setFilters(initialFilters);
    else fetchData();
  }

  const goToPage = (page) => {
    const pagination = getCurrentPagination();
    if (page >= 1 && page <= pagination.pages && page !== pagination.page) {
      setCanFetch(true);
      setFilters({ ...filters, page });
    }
  }

  const changePerPage = (perPage) => {
    setCanFetch(true);
    setFilters({ ...filters, perPage });
  }

  const filterBy = (value, target) => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      setCanFetch(true);
      setFilters({ ...filters, page: 1, [target]: value });
    }, debounceTime);
  }

  useEffect(() => {
    fetchData();
  }, [filters]);

  return {
    canResetFilter: canResetFilter(),
    changePerPage,
    data,
    filters,
    filterBy,
    goToPage,
    isLoading: !canFetch,
    pagination,
    reload,
    payments
  }
}

export default AdminPaymentsReports;
